import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import FAQ from "../components/blocks/FAQ";
import { ABOUT_FAQS, PHILOSOPHY_CHAIN } from "../data/content";

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About The Vantage Institute™ — Executive Education & Research"
        description="Why Vantage exists, what we believe, how we teach, and the Institute's relationship with The Vantage Group™ and Wowrakesh Business Academy®."
        path="/about"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-24">
          <p className="eyebrow-light">About the Institute</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            An institution built for the quality of thinking behind performance.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            The Vantage Institute™ is an executive education and research institution — a Vantage Group™
            institution, and an Executive Education & Research Division of Wowrakesh Business Academy®.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="about-why-exists">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Why Vantage Exists"
              title="Because capability, not information, is the constraint."
              intro="Leaders today are not short of content. They are short of the structured thinking, judgement and practical skill that turn information into performance. The Vantage Institute exists to close that gap — deliberately, rigorously and measurably."
            />
            <p className="mt-6 text-base leading-relaxed text-slate">
              The Institute was founded on a simple observation: most executive development is consumed, not
              applied. Certificates accumulate; capability does not. Vantage was built to be the opposite — an
              institution where everything taught is designed to be used, and where the measure of success is what
              leaders and organisations can do afterwards that they could not do before.
            </p>
          </div>
          <div>
            <SectionHeading
              eyebrow="Our Philosophy"
              title="From experience to business impact."
              intro="The Institute's philosophy is a chain — every link deliberate, none optional."
            />
            <ol className="mt-8 space-y-0 border-l-2 border-gold">
              {PHILOSOPHY_CHAIN.map((step, i) => (
                <li key={step} className="relative py-3 pl-6" data-testid={`philosophy-${step.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                  <span className="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gold" aria-hidden="true" />
                  <p className="font-sub text-sm font-semibold uppercase tracking-[0.1em] text-navy">
                    <span className="mr-2 text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                    {step}
                  </p>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm leading-relaxed text-slate">
              Experience becomes knowledge; knowledge, examined, becomes research; research, structured, becomes
              frameworks; frameworks, taught, become capability; capability, applied, becomes business impact.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-paper" data-testid="about-beliefs">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading eyebrow="What We Believe" title="Convictions that shape the Institute." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {[
              { title: "Thinking can be trained", text: "Judgement is not a gift reserved for a few. It is a discipline — and disciplines can be taught, practised and strengthened." },
              { title: "Application is the point", text: "Learning that does not change behaviour is entertainment. Every Vantage programme is engineered for use, not applause." },
              { title: "Rigour respects the learner", text: "Executives deserve content built on research and evidence, not recycled motivation. We hold our material to the standard our learners are held to." },
              { title: "Capability compounds", text: "A leader who thinks better makes better decisions for decades. Building capability is the highest-leverage investment an organisation can make." },
            ].map((b) => (
              <div key={b.title} className="card" data-testid={`belief-${b.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                <h3 className="font-sub text-base font-semibold text-navy">{b.title}</h3>
                <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
                <p className="mt-3 text-sm leading-relaxed text-slate">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="about-how-we-teach">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="How We Teach"
              title="Frameworks first. Application always."
              intro="Vantage programmes are built on the Vantage Thinking Toolbox™ — proprietary frameworks that structure how leaders analyse, decide and act. Participants work on their own live business situations, so learning transfers directly into decisions."
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="The Vantage Difference"
              title="An institution, not a vendor."
              intro="Training vendors sell courses. The Institute builds intellectual capital: research that informs frameworks, frameworks that inform programmes, and programmes that build measurable capability. The difference shows up after the programme ends — in the quality of the decisions leaders make."
            />
          </div>
        </div>
      </section>

      <section className="bg-navy" data-testid="about-relationships">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            light
            eyebrow="Institutional Relationships"
            title="Part of a wider ecosystem of advice and capability."
          />
          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            <div className="border border-white/15 bg-navy-light/40 p-6 sm:p-8" data-testid="relationship-vantage-group">
              <h3 className="font-sub text-base font-semibold text-white">The Vantage Group™</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
              <p className="mt-4 text-sm leading-relaxed text-platinum">
                The Vantage Group™ advises leaders and enterprises. The Vantage Institute™ develops the thinking,
                knowledge, capabilities and intellectual capital that make that advice possible. Advisory
                identifies what must change; the Institute builds the human capability to carry that change through.
              </p>
            </div>
            <div className="border border-white/15 bg-navy-light/40 p-6 sm:p-8" data-testid="relationship-wba">
              <h3 className="font-sub text-base font-semibold text-white">Wowrakesh Business Academy®</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
              <p className="mt-4 text-sm leading-relaxed text-platinum">
                The Vantage Institute operates as an Executive Education & Research Division of Wowrakesh Business
                Academy® — combining the Academy's educational foundation with the Institute's research mandate and
                executive development focus.
              </p>
            </div>
          </div>
          <p className="mt-10 max-w-2xl text-sm leading-relaxed text-platinum/80">
            Entity: The Vantage Institute™ — Category: Executive Education • Research • Intellectual Capital —
            Relationship: A Vantage Group™ Institution — Operating relationship: An Executive Education & Research
            Division of Wowrakesh Business Academy®.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="about-founder">
        <div className="grid items-start gap-10 lg:grid-cols-[240px_1fr] lg:gap-14">
          <img
            src="/images/rakesh-gopinathan.jpg"
            alt="Rakesh Gopinathan, Founder of The Vantage Institute™ and Founder & CEO of The Vantage Group™"
            width="240"
            height="300"
            loading="lazy"
            className="w-full max-w-[240px] border border-platinum object-cover"
          />
          <div>
            <SectionHeading
              eyebrow="Founder"
              title="Rakesh Gopinathan"
              intro="Founder & CEO of The Vantage Group™ and Wowrakesh Business Academy®, and Founder of the Vantage Institute™. A strategic business and leadership advisor, his work with founder-led and growth-stage businesses spans business strategy, cash flow, profitability, leadership, professionalisation and enterprise value — the advisory practice from which the Institute's research and programmes are built."
            />
            <Link to="/founder/rakesh-gopinathan" className="text-link mt-8 inline-flex min-h-[44px] items-center" data-testid="about-founder-link">
              Read the Founder Profile
            </Link>
          </div>
        </div>
      </section>

      <FAQ items={ABOUT_FAQS} title="Understanding the Institute" />

      <CTASection
        title="See what the Institute can build for you."
        primaryLabel="Explore Programmes"
        primaryTo="/programmes"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="about-cta"
      />
      <div className="container-x py-10 text-center">
        <Link to="/thinking-toolbox" className="text-link" data-testid="about-link-toolbox">Discover the Thinking Toolbox™</Link>
      </div>
    </>
  );
}
